import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Wallet as WalletIcon, Copy, ArrowDownToLine, ArrowUpFromLine, Coins, AlertTriangle, ExternalLink, Clock, CheckCircle2, XCircle } from "lucide-react";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return ""; } };

export default function Wallet() {
  const { checkAuth } = useAuth();
  const [data, setData] = useState(null);
  const [txid, setTxid] = useState("");
  const [amount, setAmount] = useState("");
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/wallet"); setData(data); }
    catch (e) { toast.error("Failed to load wallet"); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  const deposit = async () => {
    if (!txid.trim()) { toast.error("Paste your deposit transaction hash"); return; }
    setBusy(true);
    try {
      const { data: r } = await api.post("/wallet/deposit", { txid });
      toast.success(`Deposited ${r.credited} USDT — confirmed on-chain 🎉`);
      setTxid(""); await load(); await checkAuth();
    } catch (e) { toast.error(e.response?.data?.detail || "Deposit failed"); }
    setBusy(false);
  };

  const withdraw = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    if (!address.trim()) { toast.error("Enter your Tron (TRC-20) address"); return; }
    setBusy(true);
    try {
      await api.post("/wallet/withdraw", { amount: amt, address });
      toast.success("Withdrawal requested — paid out and confirmed instantly on the Tron (TRX) network 🎉");
      setAmount(""); setAddress(""); await load(); await checkAuth();
    } catch (e) { toast.error(e.response?.data?.detail || "Withdrawal failed"); }
    setBusy(false);
  };

  const statusBadge = (t) => {
    if (t.status === "confirmed") return <span className="flex items-center gap-1 text-green-700 font-extrabold text-xs"><CheckCircle2 size={14} /> Confirmed</span>;
    if (t.status === "pending") return <span className="flex items-center gap-1 text-orange-600 font-extrabold text-xs"><Clock size={14} /> Pending</span>;
    return <span className="flex items-center gap-1 text-red-600 font-extrabold text-xs"><XCircle size={14} /> Cancelled</span>;
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center gap-3 mb-6">
        <WalletIcon className="text-[#B8860B]" size={28} strokeWidth={2.5} />
        <h1 className="font-display font-black text-4xl tracking-tighter">Wallet</h1>
      </div>

      <div className="bg-black text-white rounded-2xl p-6 border-2 border-[#E6B31E] nb-shadow-lg mb-6">
        <div className="text-[11px] font-bold uppercase text-white/50">USDT balance</div>
        <div className="font-display font-black text-5xl text-[#E6B31E] flex items-center gap-2 mt-1" data-testid="wallet-balance">
          <Coins size={32} strokeWidth={2.5} /> {data ? data.balance_usdt.toLocaleString() : "—"}
        </div>
        <div className="text-white/40 font-semibold text-sm mt-1">Deposits & withdrawals are confirmed instantly by the public Tron (TRX) API.</div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mb-8">
        {/* Deposit */}
        <div className="bg-white nb-border rounded-2xl p-5 nb-shadow">
          <div className="flex items-center gap-2 font-display font-extrabold text-lg mb-2"><ArrowDownToLine size={18} className="text-green-600" /> Deposit USDT</div>
          {data && (
            <>
              <div className="text-xs font-bold uppercase text-black/40">Send USDT (TRC-20) to</div>
              <button onClick={() => copy(data.deposit_wallet)} data-testid="deposit-wallet" className="w-full mt-1 flex items-center justify-between gap-2 bg-black/5 rounded-lg px-3 py-2.5 text-sm font-mono break-all">
                <span className="truncate">{data.deposit_wallet}</span><Copy size={15} className="flex-shrink-0 text-[#B8860B]" />
              </button>
              <div className="mt-2 flex items-start gap-1.5 text-[11px] font-semibold text-red-700 bg-red-50 rounded-lg px-2.5 py-2">
                <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" /> Tron (TRX) / TRC-20 network only — other networks lose funds.
              </div>
              <input data-testid="deposit-txid" value={txid} onChange={(e) => setTxid(e.target.value)} placeholder="Paste transaction hash (TXID)" className="w-full mt-3 p-3 rounded-xl bg-black/5 font-medium outline-none" />
              <button data-testid="deposit-btn" disabled={busy} onClick={deposit} className="w-full mt-2 bg-green-600 text-white font-extrabold rounded-full py-3 border-2 border-black disabled:opacity-50">
                {busy ? "Verifying…" : "Confirm deposit"}
              </button>
            </>
          )}
        </div>

        {/* Withdraw */}
        <div className="bg-white nb-border rounded-2xl p-5 nb-shadow">
          <div className="flex items-center gap-2 font-display font-extrabold text-lg mb-2"><ArrowUpFromLine size={18} className="text-[#B8860B]" /> Withdraw USDT</div>
          <div className="text-xs font-bold uppercase text-black/40">Amount (min {data?.min_withdrawal ?? 1} USDT)</div>
          <input data-testid="withdraw-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" className="w-full mt-1 p-3 rounded-xl bg-black/5 font-medium outline-none" />
          <div className="text-xs font-bold uppercase text-black/40 mt-3">Your Tron (TRC-20) address</div>
          <input data-testid="withdraw-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="T..." className="w-full mt-1 p-3 rounded-xl bg-black/5 font-mono text-sm outline-none" />
          <button data-testid="withdraw-btn" disabled={busy} onClick={withdraw} className="w-full mt-3 bg-[#E6B31E] text-black font-extrabold rounded-full py-3 border-2 border-black disabled:opacity-50">
            {busy ? "Requesting…" : "Request withdrawal"}
          </button>
          <p className="text-[11px] text-black/40 font-medium mt-2">Withdrawals are paid out on the Tron (TRX) network and confirmed <span className="font-bold text-black/60">instantly</span> by the public Tron API — no 24–48 hour waits.</p>
        </div>
      </div>

      <h2 className="font-display font-extrabold text-2xl mb-3">Transaction history</h2>
      <div className="bg-white nb-border rounded-2xl nb-shadow overflow-hidden">
        {!data || data.transactions.length === 0 ? (
          <div className="p-8 text-center text-black/40 font-semibold">No transactions yet.</div>
        ) : (
          <div className="divide-y-2 divide-black/5 max-h-[420px] overflow-y-auto">
            {data.transactions.map((t) => (
              <div key={t.id} data-testid={`wallet-txn-${t.id}`} className="p-4 flex items-center gap-3">
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${t.type === "deposit" ? "bg-green-100 text-green-700" : "bg-[#FBF0CE] text-[#B8860B]"}`}>
                  {t.type === "deposit" ? <ArrowDownToLine size={18} strokeWidth={2.5} /> : <ArrowUpFromLine size={18} strokeWidth={2.5} />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-extrabold">
                    {t.type === "deposit" ? <span className="text-green-700">+{t.amount} USDT deposit</span> : <span className="text-[#B8860B]">−{t.amount} USDT withdrawal</span>}
                  </div>
                  <div className="text-xs text-black/40 font-medium truncate">{t.address ? `to ${t.address} · ` : ""}{fmtDate(t.created_at)}</div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {statusBadge(t)}
                  {t.txid && <a href={`https://tronscan.org/#/transaction/${t.txid}`} target="_blank" rel="noreferrer" className="text-black/40 hover:text-[#B8860B]" title="View on TronScan"><ExternalLink size={15} /></a>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
